<template>
    <v-dialog v-model="show" max-width="800px" :persistent="loading" scrollable>
        <v-card class="pa-4">
            <v-card-title>
                Configurações
            </v-card-title>
            <v-card-text>
                <v-form ref="formUserInfo" class="pa-2">
                    <v-row>
                        <v-col cols="12">
                            <h3 class="text-h6">Perfil do usuário</h3>
                            <p class="text-caption secondary--text">Edite as suas configurações de perfil</p>
                        </v-col>
                        <v-col cols="12">
                            <v-row>
                                <v-col cols="6">
                                    <v-text-field v-model="name" label="Name" :rules="[basic_rules.required]" />
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field v-model="email" label="Email" disabled />
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field-simplemask
                                        v-model="phone"
                                        label="Telefone/Whatsapp"
                                        :options="{
                                            inputMask: '(##) #####-####',
                                            outputMask: '###########',
                                            empty: null,
                                            applyAfter: false,
                                            alphanumeric: true,
                                            lowerCase: false,
                                        }"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field v-model="crp" label="Nº de identificação profissional" />
                                </v-col>
                                <v-col cols="12">
                                    <v-text-field v-model="address" label="Endereço para o contrato" />
                                </v-col>
                                <v-col v-if="user.user_image" cols="12">
                                    <v-card elevation="0">
                                        <v-row>
                                            <v-col cols="12">Imagem atual</v-col>
                                            <v-col cols="12" class="d-flex justify-center">
                                                <v-avatar size="200">
                                                    <v-img :src="user.user_image" />
                                                </v-avatar>
                                            </v-col>
                                        </v-row>
                                    </v-card>
                                </v-col>
                                <v-col cols="12">
                                    <v-file-input v-model="user_image" prepend-icon="mdi-camera" accept="image/*" label="Nova imagem de perfil" @change="handleFileUpload" />
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field v-model="password" label="Nova senha" :rules="password_rules" type="password"/>
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field v-model="repeat_password" label="Confirme a nova senha" :rules="repeat_password_rules" type="password" />
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                    <v-divider class="my-4"></v-divider>
                    <v-row>
                        <v-col cols="12">
                            <h3 class="text-h6">Horário de Trabalho</h3>
                            <p class="text-caption secondary--text">Configure seus horários de trabalho para cada dia da semana</p>
                        </v-col>
                        <v-col cols="12">
                            <v-simple-table dense>
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th class="text-left">Dia da Semana</th>
                                            <th class="text-center">Não atende</th>
                                            <th class="text-left">Início</th>
                                            <th class="text-left">Início Intervalo</th>
                                            <th class="text-left">Fim Intervalo</th>
                                            <th class="text-left">Fim</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="schedule in weekSchedule" :key="schedule.day_of_week">
                                            <td class="font-weight-medium">{{ getDayLabel(schedule.day_of_week) }}</td>
                                            <td class="text-center">
                                                <v-checkbox
                                                    v-model="schedule.not_attending"
                                                    dense
                                                    hide-details
                                                    class="ma-0 pa-0 d-flex justify-center"
                                                ></v-checkbox>
                                            </td>
                                            <td>
                                                <v-text-field
                                                    v-model="schedule.start_time"
                                                    :disabled="schedule.not_attending"
                                                    type="time"
                                                    dense
                                                    hide-details
                                                    class="my-1"
                                                    style="max-width: 120px;"
                                                ></v-text-field>
                                            </td>
                                            <td>
                                                <v-text-field
                                                    v-model="schedule.start_break"
                                                    :disabled="schedule.not_attending"
                                                    type="time"
                                                    dense
                                                    hide-details
                                                    class="my-1"
                                                    style="max-width: 120px;"
                                                ></v-text-field>
                                            </td>
                                            <td>
                                                <v-text-field
                                                    v-model="schedule.end_break"
                                                    :disabled="schedule.not_attending"
                                                    type="time"
                                                    dense
                                                    hide-details
                                                    class="my-1"
                                                    style="max-width: 120px;"
                                                ></v-text-field>
                                            </td>
                                            <td>
                                                <v-text-field
                                                    v-model="schedule.end_time"
                                                    :disabled="schedule.not_attending"
                                                    type="time"
                                                    dense
                                                    hide-details
                                                    class="my-1"
                                                    style="max-width: 120px;"
                                                ></v-text-field>
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </v-col>
                    </v-row>
                    <v-divider class="my-4"></v-divider>
                    <v-row>
                        <v-col cols="12" id="tour-google-sync-section">
                            <div class="d-flex align-center">
                                <h3 class="text-h6">Agenda Google</h3>
                                <v-chip v-if="!isPro && !isSysAdmin" small color="primary" class="ml-2">
                                    <v-icon left small>mdi-star</v-icon>
                                    Premium
                                </v-chip>
                            </div>
                            <p class="text-caption secondary--text">Sincronize sua agenda com o Google Calendar</p>
                        </v-col>
                        <v-col cols="12">
                            <v-card outlined class="pa-4" :disabled="!isPro && !isSysAdmin">
                                <v-overlay :value="!isPro && !isSysAdmin" absolute opacity="0.7" @click.native="showUpgradeModal('Agenda Google')">
                                    <div class="text-center" style="cursor: pointer;">
                                        <v-icon size="64" color="white">mdi-lock</v-icon>
                                        <div class="text-h6 white--text mt-2">Recurso Premium</div>
                                        <div class="text-body-2 white--text mt-1">Clique para fazer upgrade</div>
                                    </div>
                                </v-overlay>
                                <v-row>
                                    <v-col cols="12">
                                        <div v-if="!googleConnected">
                                            <v-btn color="primary" @click="handleGoogleAuth" :loading="loadingGoogle" :disabled="!isPro && !isSysAdmin">
                                                <v-icon left>mdi-google</v-icon>
                                                Vincular conta do Google
                                            </v-btn>
                                        </div>
                                        <div v-else>
                                            <div class="d-flex align-center justify-space-between mb-4">
                                                <v-alert type="success" dense text class="mb-0 flex-grow-1 mr-2">
                                                    Conta Google vinculada
                                                </v-alert>
                                                <v-btn color="error" outlined small @click="handleDisconnectGoogle" :loading="loadingGoogle">
                                                    <v-icon left small>mdi-logout</v-icon>
                                                    Desconectar
                                                </v-btn>
                                            </div>
                                            
                                            <v-row>
                                                <v-col cols="12" md="6">
                                                    <v-select
                                                        v-model="selectedCalendar"
                                                        :items="googleCalendars"
                                                        item-text="summary"
                                                        item-value="id"
                                                        label="Selecione um calendário"
                                                        outlined
                                                        dense
                                                        :loading="loadingCalendars"
                                                        :disabled="!isPro && !isSysAdmin"
                                                    >
                                                        <template v-slot:append-outer>
                                                            <v-btn icon small @click="showCreateCalendarDialog = true" :disabled="!isPro && !isSysAdmin">
                                                                <v-icon>mdi-plus</v-icon>
                                                            </v-btn>
                                                        </template>
                                                    </v-select>
                                                </v-col>
                                                <v-col cols="12" md="6">
                                                    <v-btn 
                                                        color="primary" 
                                                        @click="handleSync" 
                                                        :disabled="!selectedCalendar || !isPro && !isSysAdmin"
                                                        :loading="loadingSync"
                                                    >
                                                        <v-icon left>mdi-sync</v-icon>
                                                        Sincronizar
                                                    </v-btn>
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-divider class="my-4"></v-divider>
                    <v-row>
                        <v-col cols="12">
                            <div class="d-flex align-center">
                                <h3 class="text-h6">WhatsApp</h3>
                                <v-chip v-if="!isPro && !isSysAdmin" small color="primary" class="ml-2">
                                    <v-icon left small>mdi-star</v-icon>
                                    Premium
                                </v-chip>
                            </div>
                            <p class="text-caption secondary--text">Conecte seu WhatsApp para enviar mensagens e notificações</p>
                        </v-col>
                        <v-col cols="12">
                            <v-card outlined class="pa-4" :disabled="!isPro && !isSysAdmin">
                                <v-overlay :value="!isPro && !isSysAdmin" absolute opacity="0.7" @click.native="showUpgradeModal('WhatsApp')">
                                    <div class="text-center" style="cursor: pointer;">
                                        <v-icon size="64" color="white">mdi-lock</v-icon>
                                        <div class="text-h6 white--text mt-2">Recurso Premium</div>
                                        <div class="text-body-2 white--text mt-1">Clique para fazer upgrade</div>
                                    </div>
                                </v-overlay>
                                <v-row align="center" justify="center">
                                    <v-col cols="12" class="text-center" v-if="whatsappLoading">
                                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                                        <div class="mt-2 text-caption">Carregando...</div>
                                    </v-col>
                                    
                                    <v-col cols="12" v-else-if="whatsappConnected">
                                        <v-alert type="success" text dense class="mb-4">
                                            WhatsApp Conectado
                                        </v-alert>
                                        <v-btn color="error" outlined @click="handleDisconnectWhatsapp" :loading="whatsappLoading" :disabled="!isPro && !isSysAdmin">
                                            <v-icon left>mdi-logout</v-icon>
                                            Desconectar
                                        </v-btn>
                                    </v-col>

                                    <v-col cols="12" class="text-center" v-else>
                                        <div v-if="whatsappQrCode">
                                            <div class="text-subtitle-1 mb-2">Escaneie o QR Code com seu WhatsApp</div>
                                            <v-img :src="whatsappQrCode" max-width="250" class="mx-auto mb-4"></v-img>
                                            <v-progress-linear indeterminate color="primary" class="mb-2"></v-progress-linear>
                                            <div class="text-caption grey--text">Aguardando conexão...</div>
                                        </div>
                                        <div v-else>
                                            <v-btn color="success" @click="handleConnectWhatsapp" :loading="whatsappLoading" :disabled="!isPro && !isSysAdmin">
                                                <v-icon left>mdi-whatsapp</v-icon>
                                                Conectar WhatsApp
                                            </v-btn>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-row no-gutters justify="space-between">
                    <v-col v-if="error_message" cols="12">
                        <v-alert type="error darken-4">
                            {{ error_message }}
                        </v-alert>
                    </v-col>
                    <v-col v-if="success_message" cols="12">
                        <v-alert type="success">
                            {{ success_message }}
                        </v-alert>
                    </v-col>
                    <v-col cols="auto">
                        <v-btn :disabled="loading" :loading="loading" outlined color="secondary" @click="close"><v-icon left>mdi-arrow-u-left-top</v-icon> Voltar</v-btn>
                    </v-col>
                    <v-col cols="auto">
                        <v-btn :disabled="loading" :loading="loading" color="primary" elevation="0" @click="save">Salvar <v-icon right>mdi-check-bold</v-icon></v-btn>
                    </v-col>
                </v-row>
            </v-card-actions>
        </v-card>
        
        <!-- Create Calendar Dialog -->
        <v-dialog v-model="showCreateCalendarDialog" max-width="500px">
            <v-card>
                <v-card-title>Criar novo calendário</v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="newCalendarName"
                        label="Nome do calendário"
                        outlined
                        dense
                    ></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="showCreateCalendarDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" @click="createCalendar" :loading="loadingCreateCalendar">Criar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        
        <!-- Sync Events Dialog -->
        <v-dialog v-model="showSyncDialog" max-width="900px" scrollable>
            <v-card>
                <v-card-title>Sincronização de Agenda</v-card-title>
                <v-card-text style="max-height: 600px;">
                    <v-row>
                        <v-col cols="12" md="6">
                            <h3 class="text-subtitle-1 font-weight-bold mb-2">Eventos do Google Calendar</h3>
                            <p class="text-caption mb-4">Vincule eventos existentes do Google aos agendamentos do sistema.</p>
                            
                            <v-list dense>
                                <v-list-item v-for="(event, index) in googleEvents" :key="index" class="px-0">
                                    <v-list-item-content>
                                        <v-list-item-title class="font-weight-medium">{{ event.summary }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ formatRecurrence(event) }}</v-list-item-subtitle>
                                        <v-select
                                            v-model="eventMappings[event.id]"
                                            :items="systemEvents"
                                            item-text="title"
                                            item-value="id"
                                            label="Vincular a agendamento"
                                            outlined
                                            dense
                                            clearable
                                            hide-details
                                            class="mt-2"
                                        ></v-select>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-list-item v-if="googleEvents.length === 0">
                                    <v-list-item-content>
                                        <v-list-item-subtitle>Nenhum evento encontrado no período.</v-list-item-subtitle>
                                    </v-list-item-content>
                                </v-list-item>
                            </v-list>
                        </v-col>
                        
                        <v-divider vertical class="hidden-sm-and-down"></v-divider>
                        
                        <v-col cols="12" md="6">
                            <h3 class="text-subtitle-1 font-weight-bold mb-2">Agendamentos do Sistema</h3>
                            <p class="text-caption mb-4">Selecione quais agendamentos devem ser criados no Google Calendar.</p>
                            
                            <div v-if="unmappedSystemEvents.length > 0">
                                <v-checkbox
                                    v-model="selectAllSessions"
                                    label="Selecionar todos"
                                    dense
                                    hide-details
                                    class="mb-2"
                                    @change="toggleAllSessions"
                                ></v-checkbox>
                                <v-divider class="mb-2"></v-divider>
                                <v-list dense>
                                    <v-list-item v-for="session in unmappedSystemEvents" :key="session.id" class="px-0">
                                        <v-list-item-action class="mr-2">
                                            <v-checkbox 
                                                v-model="sessionsToCreate" 
                                                :value="session.id"
                                                dense
                                                hide-details
                                            ></v-checkbox>
                                        </v-list-item-action>
                                        <v-list-item-content>
                                            <v-list-item-title class="font-weight-medium">{{ session.title }}</v-list-item-title>
                                            <v-list-item-subtitle>
                                                {{ formatSystemEventDate(session) }}
                                            </v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>
                            </div>
                            <div v-else>
                                <p class="text-body-2 grey--text">Todos os agendamentos já estão vinculados ou não há agendamentos disponíveis.</p>
                            </div>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="showSyncDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" @click="confirmSync" :loading="loadingSync">
                        Confirmar Sincronização
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import dayjs from "dayjs";
dayjs.extend(require('dayjs/plugin/customParseFormat'));
import { mapActions, mapState, mapGetters } from 'vuex';

export default {
    props: {
        value: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            show: this.value,
            basic_rules: {
                required: value => !!value || "Informação obrigatória"
            },
            password_rules: [
                value => !value || value.length > 8 || "Mínimo de 8 caracteres",
                value => !value || /^(?=.*[a-z])(?=.*[A-Z])/.test(value) || "Precisa conter letras maiusculas e minusculas",
                value => !value || /^(?=.*\d)/.test(value) || "Precisa conter números",
                value => !value || /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value) || "Precisa conter caracteres especiais"
            ],
            repeat_password_rules: [
                value => {
                    if (this.password?.length) {
                        return this.password === value || "As senhas são diferentes"
                    } else {
                        return true;
                    }
                }
            ],
            loading: false,
            error_message: null,
            success_message: null,
            name: null,
            email: null,
            phone: null,
            crp: null,
            address: null,
            user_image: null,
            password: null,
            repeat_password: null,
            weekSchedule: [],
            loading_availability: null,
            days: [
                { text: 'Domingo', value: 'sunday' },
                { text: 'Segunda-feira', value: 'monday' },
                { text: 'Terça-feira', value: 'tuesday' },
                { text: 'Quarta-feira', value: 'wednesday' },
                { text: 'Quinta-feira', value: 'thursday' },
                { text: 'Sexta-feira', value: 'friday' },
                { text: 'Sábado', value: 'saturday' }
            ],
            // Google Calendar
            googleConnected: false,
            loadingGoogle: false,
            googleCalendars: [],
            selectedCalendar: null,
            loadingCalendars: false,
            showCreateCalendarDialog: false,
            newCalendarName: '',
            loadingCreateCalendar: false,
            showSyncDialog: false,
            googleEvents: [],
            systemEvents: [],
            eventMappings: {},
            loadingSync: false,

            tokenClient: null,
            sessionsToCreate: [],
            selectAllSessions: false,

            // WhatsApp
            whatsappConnected: false,
            whatsappQrCode: null,
            whatsappLoading: false,
            whatsappStatusInterval: null
        };
    },
    computed: {
        ...mapState("auth", {
            user: state => state.user
        }),
        ...mapGetters("auth", ["isPro", "isSysAdmin"]),
        unmappedSystemEvents() {
            // Get IDs of system events that are already mapped to a Google event
            const mappedSystemEventIds = Object.values(this.eventMappings).filter(id => id !== null && id !== undefined);
            
            // Return system events that are NOT in the mapped list
            return this.systemEvents.filter(event => !mappedSystemEventIds.includes(event.id));
        }
    },
    watch: {
        value() {
            this.show = this.value;
        },
        show() {
            if (!this.show) {
                this.$emit("close");

                this.loading = false;
                this.error_message = null;
                this.success_message = null;
                this.name = null;
                this.email = null;
                this.phone = null;
                this.crp = null;
                this.address = null;
                this.user_image = null;
                this.password = null;
                this.repeat_password = null;
                // Reset Google state but keep connection status if possible, 
                // though checking on open is safer to ensure token validity
                this.googleConnected = false; 
                this.googleCalendars = [];
                this.selectedCalendar = null;
            } else {
                this.setUser();
                this.initializeWeekSchedule();
                this.initializeWeekSchedule();
                // Só verificar Google e WhatsApp se for Pro
                if (this.isPro || this.isSysAdmin) {
                    this.checkGoogleConnection();
                    this.checkWhatsappConnection();
                }
            }

            this.error_message = null;
            this.success_message = null;
            this.$emit("input", this.show);
        },
        user() {
            this.setUser();
        }
    },
    mounted() {
        this.setUser();
        // Só inicializar Google e WhatsApp se for Pro
        if (this.isPro || this.isSysAdmin) {
            this.initGoogleClient();
            this.initGoogleClient();
            // Check WhatsApp status on mount as well, just in case
            if (this.show) {
                this.checkWhatsappConnection();
            }
        }
    },
    beforeDestroy() {
        this.stopWhatsappPolling();
    },
    methods: {
        ...mapActions("utils", [
            "ActionDoUpdateUser", 
            "ActionListAvailabilities", 
            "ActionAddAvailability", 
            "ActionUpdateAvailability", 
            "ActionDeleteAvailability",
            "ActionConnectGoogle",
            "ActionListGoogleCalendars",
            "ActionCreateGoogleCalendar",
            "ActionCreateGoogleCalendar",
            "ActionSyncGoogleEvents",
            "ActionConnectWhatsapp",
            "ActionCheckWhatsappStatus",
            "ActionDisconnectWhatsapp"
        ]),
        ...mapActions("auth", ["ActionGetCurrentUser"]),
        getDayLabel(value) {
            const day = this.days.find(d => d.value === value);
            return day ? day.text : value;
        },
        initializeWeekSchedule() {
            const daysOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const defaultTime = { start_time: '09:00', end_time: '18:00' };
            
            this.weekSchedule = daysOrder.map(day => {
                const existing = this.user?.availabilities?.find(a => a.day_of_week === day);
                return {
                    day_of_week: day,
                    start_time: existing && existing.start_time ? existing.start_time.substring(0, 5) : defaultTime.start_time,
                    end_time: existing && existing.end_time ? existing.end_time.substring(0, 5) : defaultTime.end_time,
                    start_break: existing && existing.start_break ? existing.start_break.substring(0, 5) : null,
                    end_break: existing && existing.end_break ? existing.end_break.substring(0, 5) : null,
                    id: existing?.id || null,
                    not_attending: existing ? !!existing.not_attending : true
                };
            });
        },
        setUser() {
            if (this.user) {
                this.name = this.user.name;
                this.email = this.user.email;
                this.phone = this.user.phone;
                this.crp = this.user.crp;
                this.address = this.user.address;
            }
        },
        close() {
            this.$refs.formUserInfo.reset();
            this.show = false;
        },
        async save() {
            this.loading = true;
            this.error_message = null;
            this.success_message = null;

            if (!this.$refs.formUserInfo.validate()) {
                this.loading = false;
                return;
            }

            // Validate schedule
            for (const schedule of this.weekSchedule) {
                if (!schedule.not_attending) {
                    if (!schedule.start_time || !schedule.end_time) {
                        this.error_message = `Preencha os horários para ${this.getDayLabel(schedule.day_of_week)}`;
                        this.loading = false;
                        return;
                    }

                    // Break validation
                    if (schedule.start_break || schedule.end_break) {
                        // If one is set, both must be set
                        if (!schedule.start_break || !schedule.end_break) {
                             this.error_message = `Preencha o início e fim do intervalo para ${this.getDayLabel(schedule.day_of_week)}`;
                            this.loading = false;
                            return;
                        }

                        // Validate time logic
                        if (schedule.start_break < schedule.start_time || schedule.start_break > schedule.end_time) {
                             this.error_message = `Início do intervalo deve ser entre o início e fim do expediente para ${this.getDayLabel(schedule.day_of_week)}`;
                             this.loading = false;
                             return;
                        }

                        if (schedule.end_break < schedule.start_break || schedule.end_break > schedule.end_time) {
                            this.error_message = `Fim do intervalo deve ser após o início do intervalo e antes do fim do expediente para ${this.getDayLabel(schedule.day_of_week)}`;
                            this.loading = false;
                            return;
                        }
                    }
                }
            }

            try {
                // 1. Update User Info
                await this.ActionDoUpdateUser({
                    user_id: this.user.id,
                    name: this.name,
                    phone: this.phone,
                    crp: this.crp,
                    address: this.address,
                    password: this.password,
                    user_image: this.user_image ? await this.convertImageToBase64(this.user_image) : null
                });

                // 2. Update Schedule
                const schedulePromises = this.weekSchedule.map(schedule => {
                    const payload = {
                        day_of_week: schedule.day_of_week,
                        start_time: schedule.not_attending ? null : schedule.start_time,
                        end_time: schedule.not_attending ? null : schedule.end_time,
                        start_break: schedule.not_attending ? null : schedule.start_break,
                        end_break: schedule.not_attending ? null : schedule.end_break,
                        user_id: this.user.id,
                        not_attending: schedule.not_attending
                    };

                    if (schedule.id) {
                        payload.id = schedule.id;
                        return this.ActionUpdateAvailability(payload);
                    } else {
                        return this.ActionAddAvailability(payload);
                    }
                });

                await Promise.all(schedulePromises);

                // Refresh user data
                await this.ActionGetCurrentUser();
                
                // Re-initialize the schedule with updated data
                this.initializeWeekSchedule();

                this.success_message = "Informações atualizadas com sucesso";
                this.$emit("edited");
                
            } catch (error) {
                console.error(error);
                this.error_message = error.response?.data || "Erro ao salvar informações";
            } finally {
                this.loading = false;
            }
        },
        handleFileUpload(file) {
            this.user_image = file;
        },
        convertImageToBase64(image) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    resolve(reader.result);
                };
                reader.onerror = (error) => {
                    reject(error);
                };
                reader.readAsDataURL(image);
            });
        },
        // Google Calendar Methods
        initGoogleClient() {
            // Wait for Google API to load
            setTimeout(() => {
                if (window.google && window.google.accounts) {
                    this.tokenClient = window.google.accounts.oauth2.initCodeClient({
                        client_id: '301266454377-n4bl6v82ca8uo5ea2qolc84ag3bq2tpb.apps.googleusercontent.com',
                        scope: 'https://www.googleapis.com/auth/calendar',
                        ux_mode: 'popup',
                        callback: this.handleGoogleCallback
                    });
                }
            }, 1000);
        },
        async checkGoogleConnection() {
            this.loadingGoogle = true;
            try {
                // Try to list calendars. If successful, user is connected.
                const response = await this.ActionListGoogleCalendars();
                this.googleCalendars = response.calendars || [];
                this.googleConnected = true;

                // Set default calendar if available
                if (response.defaultCalendarId) {
                    this.selectedCalendar = response.defaultCalendarId;
                } else if (this.googleCalendars.length > 0) {
                    this.selectedCalendar = this.googleCalendars[0].id;
                }
            } catch (error) {
                // If error (e.g. 401), assume not connected
                this.googleConnected = false;
                this.googleCalendars = [];
            } finally {
                this.loadingGoogle = false;
            }
        },
        handleGoogleAuth() {
            if (this.tokenClient) {
                this.loadingGoogle = true;
                this.tokenClient.requestCode();
            }
        },
        async handleGoogleCallback(response) {
            if (response.code) {
                try {
                    await this.ActionConnectGoogle(response.code);
                    this.googleConnected = true;
                    await this.loadGoogleCalendars();
                    this.success_message = "Conta Google vinculada com sucesso";
                } catch (error) {
                    console.error(error);
                    this.error_message = "Erro ao vincular conta Google";
                } finally {
                    this.loadingGoogle = false;
                }
            }
        },
        async handleDisconnectGoogle() {
            if (confirm("Tem certeza que deseja desconectar o Google Calendar? Isso irá parar a sincronização automática de eventos.")) {
                this.loadingGoogle = true;
                try {
                    await this.$store.dispatch('utils/ActionDisconnectGoogle');
                    this.googleConnected = false;
                    this.googleCalendars = [];
                    this.selectedCalendar = null;
                    this.success_message = "Conta Google desconectada com sucesso";
                } catch (error) {
                    console.error(error);
                    this.error_message = "Erro ao desconectar conta Google";
                } finally {
                    this.loadingGoogle = false;
                }
            }
        },
        async loadGoogleCalendars() {
            this.loadingCalendars = true;
            try {
                const response = await this.ActionListGoogleCalendars();
                this.googleCalendars = response.calendars || [];
                
                // Set default calendar if available
                if (response.defaultCalendarId) {
                    this.selectedCalendar = response.defaultCalendarId;
                } else if (this.googleCalendars.length > 0) {
                    // Fallback to first calendar if no default is set
                    this.selectedCalendar = this.googleCalendars[0].id;
                }
            } catch (error) {
                console.error(error);
                this.error_message = "Erro ao carregar calendários";
            } finally {
                this.loadingCalendars = false;
            }
        },
        async createCalendar() {
            if (!this.newCalendarName) {
                return;
            }
            this.loadingCreateCalendar = true;
            try {
                const response = await this.ActionCreateGoogleCalendar(this.newCalendarName);
                this.googleCalendars.push(response.calendar);
                this.selectedCalendar = response.calendar.id;
                this.showCreateCalendarDialog = false;
                this.newCalendarName = '';
                this.success_message = "Calendário criado com sucesso";
            } catch (error) {
                console.error(error);
                this.error_message = "Erro ao criar calendário";
            } finally {
                this.loadingCreateCalendar = false;
            }
        },
        async handleSync() {
            this.loadingSync = true;
            try {
                // Fetch Google events
                const response = await this.ActionSyncGoogleEvents({
                    calendarId: this.selectedCalendar,
                    action: 'fetch'
                });
                
                this.googleEvents = response.googleEvents || [];
                this.systemEvents = response.systemEvents || [];
                this.eventMappings = {};
                this.sessionsToCreate = [];
                this.selectAllSessions = false;
                
                this.showSyncDialog = true;
            } catch (error) {
                console.error(error);
                this.error_message = "Erro ao buscar eventos";
            } finally {
                this.loadingSync = false;
            }
        },
        async confirmSync() {
            this.loadingSync = true;
            try {
                await this.ActionSyncGoogleEvents({
                    calendarId: this.selectedCalendar,
                    action: 'sync',
                    mappings: this.eventMappings,
                    sessions_to_create: this.sessionsToCreate
                });
                
                this.showSyncDialog = false;
                this.success_message = "Eventos sincronizados com sucesso";
            } catch (error) {
                console.error(error);
                this.error_message = "Erro ao sincronizar eventos";
            } finally {
                this.loadingSync = false;
            }
        },
        formatEventDate(event) {
            if (event.start?.dateTime) {
                return dayjs(event.start.dateTime).format('DD/MM/YYYY HH:mm');
            } else if (event.start?.date) {
                return dayjs(event.start.date).format('DD/MM/YYYY');
            }
            return '';
        },
        formatRecurrence(event) {
            if (!event.recurrence || !event.recurrence.length) return 'Evento Único';
            
            const rrule = event.recurrence.find(r => r.startsWith('RRULE:'));
            if (!rrule) return 'Evento Recorrente';

            let freq = '';
            let interval = 1;
            
            const matchFreq = rrule.match(/FREQ=([^;]+)/);
            if (matchFreq) freq = matchFreq[1];
            
            const matchInterval = rrule.match(/INTERVAL=([^;]+)/);
            if (matchInterval) interval = parseInt(matchInterval[1], 10);

            let freqPt = 'Recorrente';
            if (freq === 'WEEKLY') {
                freqPt = interval > 1 ? `A cada ${interval} semanas` : 'Semanal';
            } else if (freq === 'MONTHLY') {
                freqPt = interval > 1 ? `A cada ${interval} meses` : 'Mensal';
            } else if (freq === 'DAILY') {
                freqPt = interval > 1 ? `A cada ${interval} dias` : 'Diário';
            } else if (freq === 'YEARLY') {
                freqPt = interval > 1 ? `A cada ${interval} anos` : 'Anual';
            }

            let time = '';
            if (event.start?.dateTime) {
                 time = ' às ' + dayjs(event.start.dateTime).format('HH:mm');
            }

            return `${freqPt}${time}`;
        },
        formatSystemEventDate(event) {
            if (event.start_time) {
                return dayjs(event.start_time).format('DD/MM/YYYY HH:mm');
            }
            return '';
        },
        toggleAllSessions() {
            if (this.selectAllSessions) {
                this.sessionsToCreate = this.unmappedSystemEvents.map(event => event.id);
            } else {
                this.sessionsToCreate = [];
            }
        },
        // WhatsApp Methods
        async checkWhatsappConnection() {
            this.whatsappLoading = true;
            try {
                const response = await this.ActionCheckWhatsappStatus();
                this.whatsappConnected = response.status === 'CONNECTED';
                if (this.whatsappConnected) {
                    this.stopWhatsappPolling();
                    this.whatsappQrCode = null;
                }
            } catch (error) {
                console.error("Error checking WhatsApp status:", error);
                // Assume disconnected on error
                this.whatsappConnected = false;
            } finally {
                this.whatsappLoading = false;
            }
        },
        async handleConnectWhatsapp() {
            this.whatsappLoading = true;
            this.whatsappQrCode = null;
            try {
                const response = await this.ActionConnectWhatsapp();
                if (response.status === 'QR_CODE_READY') {
                    this.whatsappQrCode = response.qrcode;
                    this.startWhatsappPolling();
                } else if (response.status === 'CONNECTED') {
                    this.whatsappConnected = true;
                    this.success_message = "WhatsApp já está conectado";
                }
            } catch (error) {
                console.error("Error connecting WhatsApp:", error);
                this.error_message = "Erro ao iniciar conexão com WhatsApp";
            } finally {
                this.whatsappLoading = false;
            }
        },
        async handleDisconnectWhatsapp() {
            this.whatsappLoading = true;
            try {
                await this.ActionDisconnectWhatsapp();
                this.whatsappConnected = false;
                this.whatsappQrCode = null;
                this.stopWhatsappPolling();
                this.success_message = "WhatsApp desconectado com sucesso";
            } catch (error) {
                console.error("Error disconnecting WhatsApp:", error);
                this.error_message = "Erro ao desconectar WhatsApp";
            } finally {
                this.whatsappLoading = false;
            }
        },
        startWhatsappPolling() {
            this.stopWhatsappPolling(); // Ensure no duplicate intervals
            this.whatsappStatusInterval = setInterval(async () => {
                try {
                    const response = await this.ActionCheckWhatsappStatus();
                    if (response.status === 'CONNECTED') {
                        this.whatsappConnected = true;
                        this.whatsappQrCode = null;
                        this.stopWhatsappPolling();
                        this.success_message = "WhatsApp conectado com sucesso!";
                    }
                } catch (error) {
                    console.error("Error polling WhatsApp status:", error);
                }
            }, 3000);
        },
        stopWhatsappPolling() {
            if (this.whatsappStatusInterval) {
                clearInterval(this.whatsappStatusInterval);
                this.whatsappStatusInterval = null;
            }
        },
        
        // Mostrar modal de upgrade
        showUpgradeModal(featureName) {
            if (!this.isPro && window.EventBus) {
                window.EventBus.$emit('show-upgrade-modal', {
                    feature: featureName,
                    route: null
                });
            }
        }
    }
};
</script>
